import { useTranslation } from 'next-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { Spinner } from '@phoenix/design-system/frontend/ui/spinner/spinner';

import { ProductStageCard } from '@phoenix/team-m/dashboard/frontend/ui/product-stage/product-stage-card';
import { QuickActionLink } from '@phoenix/team-m/dashboard/frontend/ui/product-stage/quick-action-link';
import { RaffleCard } from '@phoenix/team-m/dashboard/frontend/ui/product-stage/raffle-card';
import { PlaceholderCard } from '@phoenix/team-m/dashboard/frontend/shared/components/carrousel/placeholder-card';
import { Carrousel } from '@phoenix/team-m/dashboard/frontend/shared/components/carrousel/carrousel';
import { useBenefitsQuery } from '@phoenix/team-m/my-tool-shed/frontend/shared/raffle/use-benefits-query';

import { useUserProducts } from '../use-user-products';

export const ProductStage = ({ className = '', darkMode = false }) => {
  const { t } = useTranslation();
  const { raffle: flagEnabled } = useFlags();
  const { data: benefits, isFetching: isFetchingRaffles } = useBenefitsQuery();
  const {
    userProducts,
    sortedByServiceReminder,
    isLoading: isLoadingProducts,
  } = useUserProducts({ withServiceReminder: true, withManual: true });

  if (isLoadingProducts || isFetchingRaffles) {
    return (
      <div className="flex h-[416px] tablet:h-[645px] desktop:h-[612px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const raffleHundredYears = benefits?.raffles.find((raffle) => raffle.id === '100_years_stihl');

  const addPlaceholders = (cards: JSX.Element[]) => [
    <PlaceholderCard
      key="placeholder-start"
      header={t('dashboard_productStage_teaserCard_headline')}
      description={t('dashboard_productStage_teaserCard_paragraph')}
    />,
    ...(flagEnabled && raffleHundredYears?.active ? [<RaffleCard key={raffleHundredYears.id} {...raffleHundredYears} />] : []),
    ...(cards.length
      ? cards
      : [
          <PlaceholderCard
            key="placeholder-empty"
            className="justify-between !bg-primary-white"
            imageSrc="empty_state_composition_transparent.png"
            header={t('dashboard_tool_overview_empty_headline')}
            description={t('dashboard_tool_overview_empty_copyText')}
          />,
        ]),
    <PlaceholderCard key="placeholder-end" routePath="/tool-shed/add-tool">
      <QuickActionLink
        className="h-16 w-16"
        icon="icon-circle-plus.svg"
        label={t('dashboard_tool_overview_button_addTool')}
        href={link('tool-shed/add-tool')}
      />
    </PlaceholderCard>,
  ];

  return userProducts ? (
    <Carrousel
      id="product-stage"
      className={className}
      slides={addPlaceholders(sortedByServiceReminder().map((product) => <ProductStageCard key={product.id} product={product} />))}
      darkMode={darkMode}
    />
  ) : null;
};
